import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ConfigAPI from '../services/configApi';

// Resolve a config's `sidePanel` (or legacy `webEmbed`) into how the SidePanel
// should render it:
//   - A plain iframe when it carries a `url` (and no Pega-embed infrastructure).
//   - A Pega Web Embed (webEmbedConfig) otherwise (clientId/pageClass/theme/…).
// Returns { employeePaneSrc, webEmbedConfig } — exactly one is set.
const resolveSidePanel = (sidePanel) => {
  const title = (sidePanel && typeof sidePanel.title === 'string' && sidePanel.title.trim())
    ? sidePanel.title
    : null;
  if (sidePanel && typeof sidePanel.url === 'string' && sidePanel.url.trim()) {
    return { employeePaneSrc: sidePanel.url, webEmbedConfig: null, title };
  }
  return { employeePaneSrc: null, webEmbedConfig: sidePanel || null, title };
};

/**
 * BRAND vs CONFIG SEPARATION
 * 
 * CONFIGURATION (config.json):
 * - System settings: hostServer, applicationAlias, persona
 * - Backend URLs and API endpoints
 * - Channel definitions (what channels exist, their visibility)
 * - Debug mode
 * - enableSoundEffects (system-wide setting)
 * - Default messages and emails
 * - Pega Infinity version
 * 
 * BRAND (brand.json):
 * - Visual theme: colors (gradients, device bezel)
 * - Images: logos, backgrounds, icons
 * - UI text overrides: sidePanelTitle, channel titles
 * - Customer-specific: employeePaneSrc (customer homepage)
 * - Browser URL (where browser channel points)
 * - Call contacts (customer-specific phone numbers)
 * - Channel branding: custom titles, icons, colors per channel
 */

// Fields that brands can override (visual/customer-specific only)
// eslint-disable-next-line no-unused-vars
const BRAND_OVERRIDABLE_FIELDS = [
  // Visual styling
  'gradientColor1',
  'gradientColor2',
  'gradientColor3',
  'gradientColor4',
  'deviceBezelColor',
  'homeBackgroundImage',
  
  // UI text/icons
  'sidePanelTitle',
  'sidePanelIconSrc',
  
  // Customer-specific URLs
  'employeePaneSrc',  // Customer's homepage/employee portal
  'browserUrl',        // Where browser channel points
  
  // Customer-specific data
  'callContacts',      // Customer's phone numbers
  'webEmbed',          // PegaWebEmbed configuration
  
  // Channel appearance overrides (not functionality)
  'channelBranding'    // Custom titles, icons, colors for channels
];

// Helper function to merge brand over config (brand takes precedence for allowed fields)
function applyMergedConfig(state) {
  const base = state.baseConfig || {};
  const brand = state.currentBrand || {};
  
  console.log('🔄 applyMergedConfig called', { 
    hasBase: !!base, 
    hasBrand: !!brand,
    brandKeys: brand ? Object.keys(brand) : null,
    brandColors: brand?.colors,
    brandWebEmbed: brand?.webEmbed,
    currentHostServer: state.hostServer,
    currentApplicationAlias: state.applicationAlias
  });
  
  // CONFIGURATION FIELDS (protected - never overridden by brand)
  // CRITICAL: ONLY update these values if base has them. NEVER clear them if base is empty!
  // These values MUST come from the config API and should never be cleared
  if (base.hostServer) {
    let hostServer = base.hostServer;
    // Fix double /prweb if present
    if (hostServer.includes('/prweb/prweb')) {
      console.warn('⚠️ applyMergedConfig: Detected double /prweb in hostServer, fixing:', hostServer);
      hostServer = hostServer.replace('/prweb/prweb', '/prweb');
    }
    state.hostServer = hostServer;
  }
  if (base.applicationAlias) {
    state.applicationAlias = base.applicationAlias;
  }
  if (base.backendBaseURL) {
    state.backendBaseURL = base.backendBaseURL;
  }
  // Per-config LiveKit overrides (token endpoint / server url / direct token).
  state.livekit = base.livekit || null;

  // Other config fields can use fallback logic
  state.persona = base.persona || state.persona || '';
  state.pegaInfinityVersion = base.pegaInfinityVersion || '25.1.0';
  state.defaultMessages = base.defaultMessages || [];
  state.defaultEmails = base.defaultEmails || [];
  state.debug = base.debug ?? false;
  state.enableSoundEffects = base.enableSoundEffects ?? false; // Config-level setting
  // Pega backend on/off. `"pega": false` disables all Pega behaviour for this config.
  state.pegaEnabled = base.pega !== false;

  // SidePanel: plain iframe (has `url`) vs Pega Web Embed (infrastructure).
  // Use 'sidePanel' from config, fallback to 'webEmbed' for backward compatibility.
  {
    const sp = resolveSidePanel(base.sidePanel || base.webEmbed || null);
    state.webEmbedConfig = sp.webEmbedConfig;
    if (sp.employeePaneSrc) state.employeePaneSrc = sp.employeePaneSrc;
    state.configSidePanelTitle = sp.title || base.sidePanelTitle || null;
    if (state.configSidePanelTitle) state.sidePanelTitle = state.configSidePanelTitle;
  }
  
  // Channel config from base (defines what exists and their core functionality)
  state.channelConfig = base.channelConfig || {};
  
  // BRAND FIELDS - Apply colors from top-level or brand_json (individual /brand/:id endpoint
  // may wrap data inside brand_json, while the /brands list endpoint returns colors at top level)
  const brandColors = brand.colors || brand.brand_json?.colors;
  if (brandColors) {
    if (brandColors.gradientColor1 || brandColors.gradient1) {
      state.gradientColor1 = brandColors.gradientColor1 || brandColors.gradient1;
    }
    if (brandColors.gradientColor2 || brandColors.gradient2) {
      state.gradientColor2 = brandColors.gradientColor2 || brandColors.gradient2;
    }
    if (brandColors.gradientColor3 || brandColors.gradient3) {
      state.gradientColor3 = brandColors.gradientColor3 || brandColors.gradient3;
    }
    if (brandColors.gradientColor4 || brandColors.gradient4) {
      state.gradientColor4 = brandColors.gradientColor4 || brandColors.gradient4;
    }
    if (brandColors.deviceBezel) {
      state.deviceBezelColor = brandColors.deviceBezel;
    }
    console.log('🎨 Colors applied from brand (source: ' + (brand.colors ? 'brand.colors' : 'brand.brand_json.colors') + '):', {
      gradient1: state.gradientColor1,
      gradient2: state.gradientColor2,
      gradient3: state.gradientColor3,
      gradient4: state.gradientColor4,
      deviceBezel: state.deviceBezelColor
    });
  }
  
  // Apply logo - canonical key is logoUrl (camelCase); also check inside brand_json
  if (brand.logoUrl) {
    state.logoUrl = brand.logoUrl;
  } else if (brand.brand_json?.logoUrl) {
    state.logoUrl = brand.brand_json.logoUrl;
  }
  
  // Apply UI settings from nested structure
  if (brand.ui) {
    if (brand.ui.sidePanelTitle && !state.configSidePanelTitle) state.sidePanelTitle = brand.ui.sidePanelTitle;
    if (brand.ui.sidePanelIconSrc) state.sidePanelIconSrc = brand.ui.sidePanelIconSrc;
    if (brand.ui.employeePaneSrc) state.employeePaneSrc = brand.ui.employeePaneSrc;
    if (brand.ui.fontFamily) state.fontFamily = brand.ui.fontFamily;
    console.log('🎭 UI settings applied from brand.ui:', {
      sidePanelTitle: state.sidePanelTitle,
      employeePaneSrc: state.employeePaneSrc
    });
  }
  
  // Apply browser URL
  if (brand.browserUrl) {
    state.browserUrl = brand.browserUrl;
    if (state.channelConfig?.browser) {
      state.channelConfig.browser.url = brand.browserUrl;
    }
  }
  
  // Apply call contacts
  if (brand.callContacts && state.channelConfig?.call) {
    state.channelConfig.call.contacts = brand.callContacts;
  }
  
  // Apply channel branding
  if (brand.channelBranding && state.channelConfig) {
    Object.keys(brand.channelBranding).forEach(channelKey => {
      if (state.channelConfig[channelKey]) {
        const branding = brand.channelBranding[channelKey];
        // Merge branding into channel config (appearance layer)
        state.channelConfig[channelKey] = {
          ...state.channelConfig[channelKey],
          ...branding,
          // Preserve core functionality from config
          type: state.channelConfig[channelKey].type,
          visible: state.channelConfig[channelKey].visible,
          icon: branding.customIconUrl ? 'custom' : state.channelConfig[channelKey].icon
        };
      }
    });
  }
  
  // Apply webEmbed theme from brand (CRITICAL for theme support!)
  // Support both flat structure (brand.webEmbed) and nested (brand.brand_json.webEmbed)
  const webEmbedSource = brand.webEmbed || brand.brand_json?.webEmbed;
  
  if (webEmbedSource) {
    console.log('🎨 Raw brand webEmbed received:', {
      source: brand.webEmbed ? 'brand.webEmbed' : 'brand.brand_json.webEmbed',
      themeID: webEmbedSource.themeID,
      hasTheme: !!webEmbedSource.theme,
      themeKeys: webEmbedSource.theme ? Object.keys(webEmbedSource.theme) : null,
      fullTheme: webEmbedSource.theme ? JSON.stringify(webEmbedSource.theme).substring(0, 200) : null
    });
    
    // DEEP CLONE the theme to ensure Redux detects changes and nested properties are preserved
    state.webEmbedBranding = {
      themeID: webEmbedSource.themeID,
      theme: webEmbedSource.theme ? JSON.parse(JSON.stringify(webEmbedSource.theme)) : undefined
    };
    
    console.log('🎨 WebEmbed branding set in state:', {
      hasThemeID: !!state.webEmbedBranding.themeID,
      hasTheme: !!state.webEmbedBranding.theme,
      themeKeys: state.webEmbedBranding.theme ? Object.keys(state.webEmbedBranding.theme) : null,
      fullTheme: state.webEmbedBranding.theme ? JSON.stringify(state.webEmbedBranding.theme).substring(0, 200) : null
    });
  } else {
    // Clear webEmbed branding if brand doesn't provide it
    state.webEmbedBranding = null;
    console.log('🧹 WebEmbed branding cleared (brand has no webEmbed in brand.webEmbed or brand.brand_json.webEmbed)');
  }
  
  // Apply DXA configuration from brand
  if (brand.dxa) {
    state.dxaConfig = {
      enabled: brand.dxa.enabled !== false, // Default to true if not specified
      baseUrl: brand.dxa.baseUrl,
      defaultAction: brand.dxa.defaultAction || 'openPage',
      defaultPageId: brand.dxa.defaultPageId || 'pyHome',
      defaultPageClass: brand.dxa.defaultPageClass || 'Data-Portal',
      authFlow: brand.dxa.authFlow || 'confidential',
      useBackendProxy: brand.dxa.useBackendProxy || false
    };
    console.log('🌐 DXA config set from brand:', state.dxaConfig);
  } else {
    // Clear DXA config if brand doesn't provide it
    state.dxaConfig = null;
    console.log('🧹 DXA config cleared (brand has no dxa)');
  }
  
  console.log('✅ applyMergedConfig completed - Final infrastructure values:', {
    hostServer: state.hostServer,
    applicationAlias: state.applicationAlias,
    backendBaseURL: state.backendBaseURL,
    hasWebEmbedConfig: !!state.webEmbedConfig,
    hasWebEmbedBranding: !!state.webEmbedBranding
  });
  console.log('✅ applyMergedConfig complete', {
    gradients: [state.gradientColor1, state.gradientColor2, state.gradientColor3, state.gradientColor4],
    browserUrl: state.browserUrl,
    employeePaneSrc: state.employeePaneSrc,
    callContacts: state.channelConfig?.call?.contacts?.length || 0,
    hasWebEmbedBranding: !!state.webEmbedBranding,
    webEmbedBrandingPreview: state.webEmbedBranding ? JSON.stringify(state.webEmbedBranding).substring(0, 100) : null
  });
}

// Async thunk to fetch configuration from API
export const fetchConfiguration = createAsyncThunk(
  'channel/fetchConfiguration',
  async (configId, { rejectWithValue }) => {
    try {
      return await ConfigAPI.getConfiguration(configId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch brand (separate from config)
export const fetchBrand = createAsyncThunk(
  'channel/fetchBrand',
  async (brandId, { rejectWithValue }) => {
    try {
      console.log('🎨 fetchBrand thunk - START, brandId:', brandId);
      
      if (!brandId) {
        console.log('🎨 fetchBrand thunk - No brandId provided, returning null');
        return null;
      }
      
      console.log('🎨 fetchBrand thunk - Calling ConfigAPI.getBrand...');
      const brand = await ConfigAPI.getBrand(brandId);
      
      // CRITICAL FIX: API doesn't return the id in the brand object, so we add it here
      if (brand) {
        brand.id = brandId;
        console.log('✅ fetchBrand thunk - Added id to brand object:', brandId);
      }
      
      console.log('🎨 fetchBrand thunk - Got brand from API:', {
        hasBrand: !!brand,
        brandId: brand?.id,
        brandName: brand?.ui?.sidePanelTitle,
        brandKeys: brand ? Object.keys(brand) : null
      });
      
      // CRITICAL: Save to localStorage HERE in the thunk (not in the reducer)
      // Reducers must be pure - side effects like localStorage go in thunks
      if (brand && brand.id !== undefined && brand.id !== null) {
        const brandIdString = brand.id.toString();
        localStorage.setItem('selectedBrandId', brandIdString);
        console.log('💾 fetchBrand thunk - SAVED brand to localStorage:', brandIdString);
      } else {
        console.warn('⚠️ fetchBrand thunk - NOT saving to localStorage because brand is null or has no id');
      }
      
      console.log('🎨 fetchBrand thunk - RETURNING brand with id:', brand?.id);
      return brand;
    } catch (error) {
      console.error('❌ fetchBrand thunk - ERROR:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to list available brands (for switcher UI)
// Enriches each brand with full brand_json by fetching individual brand details
export const fetchAvailableBrands = createAsyncThunk(
  'channel/fetchAvailableBrands',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ConfigAPI.listBrands();
      const brands = response.items || response;
      console.log('📋 fetchAvailableBrands - API response:', { 
        rawResponse: response, 
        brands, 
        brandsCount: Array.isArray(brands) ? brands.length : 'not an array'
      });
      
      // /brands endpoint now returns logoUrl directly — no per-brand fetch needed
      console.log('✅ Brands loaded:', brands.length);
      return brands;
    } catch (error) {
      console.error('❌ fetchAvailableBrands failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to list available configurations (for settings modal)
export const fetchAvailableConfigurations = createAsyncThunk(
  'channel/fetchAvailableConfigurations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ConfigAPI.listConfigurations();
      const configurations = response.items || response;
      console.log('📋 fetchAvailableConfigurations - API response:', { 
        rawResponse: response, 
        configurations, 
        configurationsCount: Array.isArray(configurations) ? configurations.length : 'not an array'
      });
      return configurations; // Handle both {items: []} and direct array responses
    } catch (error) {
      console.error('❌ fetchAvailableConfigurations failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch configuration using centralized config manager
export const fetchConfig = createAsyncThunk(
  'config/fetch',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const configId = +params.get('config');
      const customerId = +params.get('customer'); // Support customer parameter
      
      // Fetch from API if config parameter exists
      if (configId) {
        console.log('📡 Fetching config from API:', { configId, customerId });
        
        // Fetch configuration
        const config = await dispatch(fetchConfiguration(configId)).unwrap();
        
        // Fetch brand if customer parameter is specified
        if (customerId) {
          await dispatch(fetchBrand(customerId)).unwrap();
        }
        
        // Fetch available brands for switcher
        await dispatch(fetchAvailableBrands()).unwrap();
        
        return config;
      }

      const configData = {};
      return configData;
    } catch (err) {
      console.error("Could not load config", err);
      return rejectWithValue(err.message);
    }
  }
);

// NEW: Initialize from database with both config and brand
export const initializeFromDatabase = createAsyncThunk(
  'config/initializeFromDatabase',
  async ({ configId, brandId, skipReset = false }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log('🚀 Initializing from database:', { configId, brandId, skipReset });

      // Reset agent/channel state when explicitly changing config, but NOT on app init/refresh
      if (!skipReset) {
        console.log('🧹 Resetting channel state before loading new config/brand');
        dispatch({ type: 'channel/resetState' });
        // CRITICAL: Also clear live events from Redux when switching configs
        dispatch({ type: 'live/clearLiveEvents' });
        
        // Verify the clear worked
        const stateAfterClear = getState();
        console.log('✅ After clearLiveEvents - Redux live.eventHistory length:', stateAfterClear.live?.eventHistory?.length || 0);
      } else {
        console.log('⏭️ Skipping reset - loading saved config on app init');
      }

      // Fetch configuration first
      const config = await dispatch(fetchConfiguration(configId)).unwrap();
      console.log('✅ Config fetched:', config);

      // Fetch brand
      const brand = await dispatch(fetchBrand(brandId)).unwrap();
      console.log('✅ Brand fetched:', brand);

      // CRITICAL: Apply the brand to state (this is what BrandModal does!)
      dispatch(applyBrand(brand));
      console.log('✅ Brand applied to state');

      // IMPORTANT: Also fetch available brands and configurations to populate the arrays
      // This ensures the config button visibility logic works correctly for returning users
      await dispatch(fetchAvailableBrands()).unwrap();
      await dispatch(fetchAvailableConfigurations()).unwrap();
      console.log('✅ Fetched available brands and configurations for UI state');

      // Save selections to localStorage
      localStorage.setItem('selectedConfigId', configId.toString());
      localStorage.setItem('selectedBrandId', brandId.toString());

      console.log('✅ Successfully initialized from database');
      return { config, brand };

    } catch (err) {
      console.error('❌ Failed to initialize from database:', err);
      return rejectWithValue(err.message);
    }
  }
);

export const revertToDefaults = createAsyncThunk(
  'config/revert',
  async (_, { dispatch }) => {
    // A list of all keys that are used for storing config overrides
    const configKeys = [
      'hostServer',
      'applicationAlias',
      'persona',
      'channelConfigOverrides',
      'defaultMessages',
      'defaultEmails',
      'pegaInfinityVersion',
      'deviceBezelColor'
    ];

    // Clear all relevant localStorage items
    configKeys.forEach(key => localStorage.removeItem(key));
    
    // After clearing, re-fetch the original configuration to reset the state
    await dispatch(fetchConfig());
  }
);

// `?hideiOSHeader=true` (or `=1`) hides the simulated iOS status bar at boot.
// `?hideiOSHeader=false` (or `=0`) forces it on. Absent param = default (on).
const readInitialShowMobileStatusBar = () => {
  if (typeof window === 'undefined') return true;
  const raw = new URLSearchParams(window.location.search).get('hideiOSHeader');
  if (raw === null) return true;
  const normalized = raw.toLowerCase();
  if (normalized === 'true' || normalized === '1') return false;
  if (normalized === 'false' || normalized === '0') return true;
  return true;
};

const initialState = {
  currentChannel: 'home',
  channels: ['email'], // Will be populated from config
  channelConfig: {}, // Populated by fetchConfig
  messages: [],
  emails: [],
  defaultEmails: [],
  defaultMessages: [],
  status: 'idle',
  error: null,
  isConfigModalOpen: false,
  isHelpModalOpen: false,
  hostServer: '',
  applicationAlias: '',
  persona: '',
  
  // Configuration and brand state
  configId: null,
  configSource: 'local', // 'local' or 'api'
  customerId: null,
  baseConfig: null, // Raw config from /api/v1/configuration/{id}
  currentBrand: null, // Raw brand from /api/v1/brand/{id}
  availableBrands: [], // List of available brands from /api/v1/brands
  availableConfigurations: [], // List of available configurations from /api/v1/configurations
  configStatus: 'idle', // 'idle' | 'loading' | 'loaded' | 'error'
  brandStatus: 'idle', // 'idle' | 'loading' | 'loaded' | 'error'
  configError: null,
  brandError: null,
  
  resetCounter: 0, // Add a counter to trigger resets in components
  smsJustReceived: false, // Flag to control typing animation
  pendingMessage: null, // To hold a new SMS before it's displayed
  selectedEmailId: null,
  isViewingEmail: false,
  newlyReceivedEmailId: null,
  isCallInProgress: false,
  callType: 'voice', // 'voice' or 'video'
  activeCallContact: null,
  calendarDate: null, // Add this to store the date for the calendar animation
  lastAnimatedCalendarDate: null, // Track last animated calendar date globally
  agentMessages: [],
  agentStatus: 'idle',
  isListening: false,
  isThinking: false,
  isSpeaking: false,
  followMode: false, // Default to false
  isVibrating: false,
  pegaInfinityVersion: '25.1.0',
  callInfo: null, // To store info about an incoming call
  browserUrl: null, // <-- add this to initialState
  appUrl: null,
  deviceBezelColor: '#1a1a2e',

  gradientColor1: '#04142C',
  gradientColor2: '#1e3a8a',
  gradientColor3: '#23a6d5',
  gradientColor4: '#23d5ab',

  calendarUpdateFromWebSocket: false,
  pegaContextID: null,
  pegaConversationID: null,
  pegaAccessToken: null,
  debug: false, // Add debug to initialState
  enableSoundEffects: false, // Add sound effects toggle to initialState
  brandName: '', // Organisation name from the brand's DB brand_name column
  sidePanelTitle: '', // Add this to initialState
  // Config-level side-panel title override (config.sidePanel.title or top-level
  // config.sidePanelTitle). When set, it wins over a brand's ui.sidePanelTitle.
  configSidePanelTitle: null,
  sidePanelIconSrc: '', // Add side panel icon source to initialState
  backendBaseURL: '', // Add backend base URL to initialState
  // Optional per-config LiveKit overrides (e.g. different LiveKit for non-Pega
  // configs). Shape: { tokenUrl?, url?, token? }. See VideoCallView. The API
  // secret is NEVER put here — token minting stays server-side (tokenUrl).
  livekit: null,
  homeBackgroundImage: '', // Add home background image to initialState
  showMobileStatusBar: readInitialShowMobileStatusBar(), // Toggle for showing/hiding iOS status bar on mobile/tablet; can be initialized via ?hideiOSHeader=true/false
  employeeEventsEnabled: true, // When false, incoming sidepanel/employee events from the agent are ignored (side panel does not auto-open)
  iPadDesktopLandscape: (typeof window === 'undefined' || localStorage.getItem('iPadDesktopLandscape') !== 'false'), // Trial: render desktop UI on iPad in landscape. Defaults ON; the toggle writes 'false' on opt-out.
  
  // SidePanel / PegaWebEmbed configuration
  employeePaneSrc: null, // URL for iframe in side panel
  employeePegaWebEmbedProps: null, // Props for PegaWebEmbed component in SidePanel
  appPegaWebEmbedProps: null, // Props for PegaWebEmbed component in AppView
  webEmbedConfig: null, // Infrastructure settings (OAuth, URLs) from config.sidePanel
  webEmbedBranding: null, // Theme settings (dark/light) from config.webEmbed or brand
  // Whether this config uses the Pega backend at all. Set `"pega": false` in a
  // config to fully disable Pega (health check, case resolver, web embed) — e.g.
  // for configs that only use a plain iframe side panel. Defaults true.
  pegaEnabled: true,
  
  // Onboarding state
  onboardingEvents: [], // Events to show on first load from config API
  hasSeenOnboarding: localStorage.getItem('hasSeenOnboarding') === 'true', // Track if user has completed onboarding
  onboardingCallEvent: null, // Pending call event from onboarding config (type: 'call')
  onboardingTourSteps: null, // Pending tour steps from onboarding config (type: 'tour')
  onboardingDefaultTourPending: false, // Pending request to start the built-in guided tour (type: 'default-tour')
  isSettingsModalOpen: false, // Track if SettingsModal is currently open (for tour coordination)
  isBrandSwitcherOpen: false, // Track if BrandSwitcher modal is currently open (for tour coordination)

  // Navigation history for mobile back button
  channelHistory: [],
};

const channelSlice = createSlice({
  name: 'channel',
  initialState,
  reducers: {
    setChannel(state, action) {
      // Support both string payload (legacy) and object payload with options
      const channel = typeof action.payload === 'string' ? action.payload : action.payload.channel;
      const isManual = typeof action.payload === 'object' ? action.payload.manual : false;
      
      // Ensure unreadCounts exists
      if (!state.unreadCounts) {
        state.unreadCounts = { sms: 0, email: 0 };
      }
      
      // Reset unread count when opening a channel
      if (state.unreadCounts[channel] !== undefined) {
        state.unreadCounts[channel] = 0;
      }
      
      // CRITICAL FIX: When manually navigating to app channel (e.g., clicking App button from Home),
      // clear navigation params to load the default view.
      // When navigating from WebSocket events or link clicks, preserve the navigation params.
      if (channel === 'app' && isManual) {
        console.log('🏠 Manual navigation to app channel - clearing navigation params to load default');
        state.appUrl = null;
        state.appPegaWebEmbedProps = null;
        state.dxaProps = null;
      } else if (channel === 'app') {
        console.log('🔗 Automated navigation to app channel - preserving any navigation params from WebSocket/link');
      }
      
      // Save previous channel for back navigation before overwriting
      const previousChannel = state.currentChannel;
      
      state.currentChannel = channel;
      state.animationKey = (state.animationKey || 0) + 1;

      // Push previous channel to history for mobile back navigation
      if (state.channelHistory === undefined) state.channelHistory = [];
      if (previousChannel && previousChannel !== channel) {
        if (state.channelHistory.length >= 20) state.channelHistory.shift();
        state.channelHistory.push(previousChannel);
      }
    },
    goBack(state) {
      if (state.channelHistory && state.channelHistory.length > 0) {
        state.currentChannel = state.channelHistory.pop();
        state.animationKey = (state.animationKey || 0) + 1;
      }
    },
    setFollowMode(state, action) {
      state.followMode = action.payload;
    },
    toggleFollowMode(state) {
      state.followMode = !state.followMode;
    },
    toggleMobileStatusBar(state) {
      state.showMobileStatusBar = !state.showMobileStatusBar;
    },
    toggleEmployeeEvents(state) {
      state.employeeEventsEnabled = !state.employeeEventsEnabled;
    },
    toggleIPadDesktopLandscape(state) {
      state.iPadDesktopLandscape = !state.iPadDesktopLandscape;
      // Mirror to localStorage so shouldUseMobileUI() (called from non-React
      // code paths) sees the flag without needing the redux store. Both
      // values are persisted explicitly — absence is treated as ON, so we
      // must write 'false' on opt-out (not removeItem). Fire a synthetic
      // resize as a belt-and-braces nudge for any non-React listeners.
      if (typeof window !== 'undefined') {
        localStorage.setItem('iPadDesktopLandscape', state.iPadDesktopLandscape ? 'true' : 'false');
        window.dispatchEvent(new Event('resize'));
      }
    },
    updateConfig(state, action) {
      const { hostServer, applicationAlias, persona, pegaInfinityVersion, deviceBezelColor, gradientColor1, gradientColor2, gradientColor3, gradientColor4, debug, backendBaseURL, sidePanelTitle } = action.payload;
      
      // CRITICAL: Only update infrastructure values if they are explicitly provided
      // Never set them to undefined if they're not in the payload
      if (hostServer !== undefined) {
        state.hostServer = hostServer;
      }
      if (applicationAlias !== undefined) {
        state.applicationAlias = applicationAlias;
      }
      if (persona !== undefined) {
        state.persona = persona;
      }
      if (sidePanelTitle !== undefined) {
        state.sidePanelTitle = sidePanelTitle;
      }
      // All config values now come from database, no localStorage needed
      if (typeof debug === 'boolean') {
        state.debug = debug;
      }
      if (pegaInfinityVersion) {
        state.pegaInfinityVersion = pegaInfinityVersion;
      }
      if (deviceBezelColor) {
        state.deviceBezelColor = deviceBezelColor;
      }
      if (gradientColor1) {
        state.gradientColor1 = gradientColor1;
      }
      if (gradientColor2) {
        state.gradientColor2 = gradientColor2;
      }
      if (gradientColor3) {
        state.gradientColor3 = gradientColor3;
      }
      if (gradientColor4) {
        state.gradientColor4 = gradientColor4;
      }
      if (backendBaseURL) {
        state.backendBaseURL = backendBaseURL;
      }
    },
    setSidePanelTitle(state, action) {
      const sidePanelTitle = action.payload;
      state.sidePanelTitle = sidePanelTitle;
    },
    setSidePanelIconSrc(state, action) {
      const sidePanelIconSrc = action.payload;
      state.sidePanelIconSrc = sidePanelIconSrc;
    },
    updateChannelConfig(state, action) {
      // Update channelConfig by merging the payload
      state.channelConfig = { ...state.channelConfig, ...action.payload };
      // Persist only the overrides to localStorage
      const storedOverrides = JSON.parse(localStorage.getItem('channelConfigOverrides')) || {};
      const newOverrides = { ...storedOverrides, ...action.payload };
      localStorage.setItem('channelConfigOverrides', JSON.stringify(newOverrides));
    },
    updateDefaultContent(state, action) {
      const { defaultMessages, defaultEmails } = action.payload;
      // Default content now comes from database, not localStorage
      state.defaultMessages = defaultMessages;
      state.defaultEmails = defaultEmails;
      // Also update the live message lists to reflect the new defaults immediately
      state.messages = defaultMessages;
      state.emails = defaultEmails;
    },
    toggleConfigModal(state) {
      state.isConfigModalOpen = !state.isConfigModalOpen;
    },
    toggleHelpModal(state) {
      state.isHelpModalOpen = !state.isHelpModalOpen;
    },
    // Apply brand to current state
    applyBrand: (state, action) => {
      const brand = action.payload;
      
      console.log('🎨 applyBrand reducer called with:', brand);
      
      if (!brand) {
        console.log('🔄 Clearing brand, resetting to config defaults');
        state.currentBrand = null;
        state.customerId = null;
        state.brandName = '';
        localStorage.removeItem('selectedBrandId');
        applyMergedConfig(state);
        return;
      }
      
      // Store the brand object
      state.currentBrand = brand;
      state.customerId = brand.id;
      
      // brand_name DB column — available on the enriched brand object from availableBrands
      if (brand.name) {
        state.brandName = brand.name;
        console.log('🏷️ applyBrand - brandName set:', state.brandName);
      }
      
      // Persist brand selection to localStorage (only if brand has an ID)
      if (brand.id !== undefined && brand.id !== null) {
        localStorage.setItem('selectedBrandId', brand.id.toString());
        console.log('💾 Saved brand to localStorage:', brand.id);
      } else {
        console.warn('⚠️ Brand has no ID, cannot persist to localStorage');
      }
      
      console.log('📝 Applying brand overrides for visual/customer-specific fields');
      
      /**
       * Brand structure from API:
       * {
       *   id: 2,
       *   name: "Bank of Ireland",
       *   logo_url: "https://...",
       *   colors: { gradient1: "#0000f4", gradient2: "#0502c0", ... },
       *   images: { homeBackground: "https://...", logo: "https://..." },
       *   ui: { sidePanelTitle: "...", sidePanelIconSrc: "...", employeePaneSrc: "https://..." },
       *   channelBranding: { agent: { title: "BOI Compass", color: "#0000f4", ... } },
       *   browserUrl: "https://bankofireland.com",
       *   callContacts: [{ name: "BOI Support", number: "555-0101", ... }]
       * }
       */
      
      // Apply color overrides
      if (brand.colors) {
        if (brand.colors.gradient1) state.gradientColor1 = brand.colors.gradient1;
        if (brand.colors.gradient2) state.gradientColor2 = brand.colors.gradient2;
        if (brand.colors.gradient3) state.gradientColor3 = brand.colors.gradient3;
        if (brand.colors.gradient4) state.gradientColor4 = brand.colors.gradient4;
        if (brand.colors.deviceBezel) state.deviceBezelColor = brand.colors.deviceBezel;
        console.log('🎨 Colors applied:', {
          gradient1: state.gradientColor1,
          gradient2: state.gradientColor2,
          gradient3: state.gradientColor3,
          gradient4: state.gradientColor4,
          deviceBezel: state.deviceBezelColor
        });
      }
      
      // Apply image overrides
      if (brand.images) {
        if (brand.images.logo) state.logoUrl = brand.images.logo;
        console.log('🖼️ Images applied:', {
          logo: state.logoUrl
        });
      }
      
      // Apply UI overrides
      if (brand.ui) {
        if (brand.ui.sidePanelTitle && !state.configSidePanelTitle) state.sidePanelTitle = brand.ui.sidePanelTitle;
        if (brand.ui.sidePanelIconSrc) state.sidePanelIconSrc = brand.ui.sidePanelIconSrc;
        if (brand.ui.employeePaneSrc) state.employeePaneSrc = brand.ui.employeePaneSrc;
        console.log('🎭 UI overrides applied:', {
          sidePanelTitle: state.sidePanelTitle,
          sidePanelIconSrc: state.sidePanelIconSrc,
          employeePaneSrc: state.employeePaneSrc
        });
      }
      
      // Apply browser URL (customer-specific)
      if (brand.browserUrl) {
        state.browserUrl = brand.browserUrl;
        if (state.channelConfig?.browser) {
          state.channelConfig.browser.url = brand.browserUrl;
        }
        console.log('🌐 Browser URL applied:', state.browserUrl);
      }
      
      // Apply call contacts (customer-specific)
      if (brand.callContacts && state.channelConfig?.call) {
        state.channelConfig.call.contacts = brand.callContacts;
        console.log('📞 Call contacts applied:', brand.callContacts.length);
      }
      
      // Apply channel branding (appearance overrides for channels).
      // Support both the flat shape (brand.channelBranding) and the nested shape
      // (brand.brand_json.channelBranding) — matching how webEmbed is resolved
      // below. Without this, brands that nest channelBranding under brand_json
      // never update the email sender name / SMS title+subtitle on a brand switch.
      const channelBranding = brand.channelBranding || brand.brand_json?.channelBranding;
      if (channelBranding && state.channelConfig) {
        Object.keys(channelBranding).forEach(channelKey => {
          if (state.channelConfig[channelKey]) {
            const branding = channelBranding[channelKey];
            console.log(`🎨 Applying branding for channel: ${channelKey}`, branding);
            
            // Merge branding into channel config (appearance only)
            // For agent/app channels: customIconUrl falls back to top-level logoUrl
            const brandLogoUrl = brand.logoUrl;
            const resolvedCustomIconUrl = branding.customIconUrl || 
              ((channelKey === 'agent' || channelKey === 'app') ? brandLogoUrl : undefined);
            state.channelConfig[channelKey] = {
              ...state.channelConfig[channelKey],
              // Branding fields
              title: branding.title || state.channelConfig[channelKey].title,
              subtitle: branding.subtitle,
              color: branding.color,
              customIconUrl: resolvedCustomIconUrl,
              avatar: branding.avatar || ((channelKey === 'agent') ? brandLogoUrl : undefined),
              splashScreenImage: branding.splashScreenImage,
              splashScreenText: branding.splashScreenText,
              splashScreenBackground: branding.splashScreenBackground,
              splashScreenForeground: branding.splashScreenForeground,
              // Email-specific: sender name from brand branding
              senderName: branding.senderName || state.channelConfig[channelKey].senderName,
              // Agent-specific: gradient colors for orb rings
              gradientColor1: branding.gradientColor1,
              gradientColor2: branding.gradientColor2,
              gradientColor3: branding.gradientColor3,
              gradientColor4: branding.gradientColor4,
              fontFamily: branding.fontFamily,
              // Agent-specific: background color (can be gradient or solid)
              backgroundColor: branding.backgroundColor,
              // Preserve core functionality from config
              type: state.channelConfig[channelKey].type,
              visible: state.channelConfig[channelKey].visible,
              icon: resolvedCustomIconUrl ? 'custom' : state.channelConfig[channelKey].icon
            };
          }
        });
        console.log('✅ Channel branding applied');
      }
      
      // Apply webEmbed theme from brand (if provided)
      // Support both flat structure (brand.webEmbed) and nested (brand.brand_json.webEmbed)
      const webEmbedSource = brand.webEmbed || brand.brand_json?.webEmbed;
      
      if (webEmbedSource) {
        console.log('🎨 Raw brand webEmbed received from API (applyBrand):', {
          source: brand.webEmbed ? 'brand.webEmbed' : 'brand.brand_json.webEmbed',
          themeID: webEmbedSource.themeID,
          hasTheme: !!webEmbedSource.theme,
          themeKeys: webEmbedSource.theme ? Object.keys(webEmbedSource.theme) : null,
          fullTheme: webEmbedSource.theme ? JSON.stringify(webEmbedSource.theme) : null
        });
        
        // Create a completely new object to ensure Redux detects the change
        // DEEP CLONE the theme object to handle nested structures (e.g., theme.base.shadow.focus)
        state.webEmbedBranding = {
          themeID: webEmbedSource.themeID,
          theme: webEmbedSource.theme ? JSON.parse(JSON.stringify(webEmbedSource.theme)) : undefined
        };
        console.log('🎨 WebEmbed branding applied after deep clone:', {
          hasThemeID: !!state.webEmbedBranding.themeID,
          hasTheme: !!state.webEmbedBranding.theme,
          themeKeys: state.webEmbedBranding.theme ? Object.keys(state.webEmbedBranding.theme) : null,
          themePreview: state.webEmbedBranding.theme ? JSON.stringify(state.webEmbedBranding.theme).substring(0, 200) : null,
          fullTheme: state.webEmbedBranding.theme ? JSON.stringify(state.webEmbedBranding.theme) : null
        });
      } else {
        // Clear webEmbed branding if brand doesn't provide it
        state.webEmbedBranding = null;
        console.log('🧹 WebEmbed branding cleared (brand has no webEmbed config in brand.webEmbed or brand.brand_json.webEmbed)');
      }
      
      // Re-process existing emails with the new sender name from the brand.
      // This updates all emails currently displayed when switching brands.
      // Fall back to the brand name (not a generic default) so the sender still
      // reflects the brand when a brand omits channelBranding.email.senderName.
      const emailChannelConfig = state.channelConfig?.email || {};
      const newSenderName = emailChannelConfig.senderName || state.brandName || "Customer Services";
      
      console.log('📧 Email re-processing:', {
        emailChannelConfig,
        emailChannelConfigKeys: Object.keys(emailChannelConfig),
        newSenderName,
        emailsCount: state.emails?.length || 0
      });
      
      if (state.emails && state.emails.length > 0) {
        state.emails = state.emails.map(email => ({
          ...email,
          sender: { name: newSenderName }
        }));
        console.log('📧 Updated existing emails with new sender name:', newSenderName, '- count:', state.emails.length);
        // Persist updated emails to localStorage
        localStorage.setItem('email_messages', JSON.stringify(state.emails));
      }
      
      console.log('✅ Brand fully applied. Current visual state:', {
        gradients: [state.gradientColor1, state.gradientColor2, state.gradientColor3, state.gradientColor4],
        deviceBezelColor: state.deviceBezelColor,
        sidePanelTitle: state.sidePanelTitle,
        homeBackgroundImage: state.homeBackgroundImage,
        browserUrl: state.browserUrl,
        employeePaneSrc: state.employeePaneSrc,
        hasCallContacts: !!state.channelConfig?.call?.contacts?.length,
        emailSenderName: newSenderName
      });
    },
    // Clear brand (reset to defaults)
    clearBrand: (state) => {
      state.currentBrand = null;
      state.customerId = null;
      // Remove brand from localStorage
      localStorage.removeItem('selectedBrandId');
      console.log('🧹 Cleared brand from localStorage');
      // Re-apply merged config without brand
      applyMergedConfig(state);
    },
    // Set base config from API (applies merge with current brand)
    setBaseConfig: (state, action) => {
      state.baseConfig = action.payload;
      state.configStatus = 'loaded';
      applyMergedConfig(state);
    },
    // Set current brand from API (applies merge over base config)
    setCurrentBrand: (state, action) => {
      state.currentBrand = action.payload;
      state.brandStatus = 'loaded';
      applyMergedConfig(state);
    },
    // Set available brands list
    setAvailableBrands: (state, action) => {
      state.availableBrands = action.payload;
    },
    // Set available configurations list
    setAvailableConfigurations: (state, action) => {
      state.availableConfigurations = action.payload;
    },
    resetState(state) {
      // Reset the live lists to the current defaults.
      // These defaults might have been updated in the config modal.
      state.messages = state.defaultMessages || [];
      state.emails = state.defaultEmails || [];
      state.agentMessages = [];

      // Reset calendar date to today
      const today = new Date();
      const todayString = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      state.calendarDate = todayString;
      state.calendarUpdateFromWebSocket = false; // Reset WebSocket flag

      // Reset agent state completely
      state.agentStatus = 'idle';
      state.pendingMessage = '';
      state.pegaConversationID = null;
      
      // Reset call state
      state.isCallInProgress = false;
      state.callInfo = null;
      state.activeCallContact = null;

      // Now, persist this reset state back to localStorage.
      // This ensures that if the user refreshes, the app loads the cleared state.
      localStorage.setItem('sms_messages', JSON.stringify(state.messages));
      localStorage.setItem('email_messages', JSON.stringify(state.emails));
      localStorage.setItem('agent_messages', JSON.stringify(state.agentMessages));
      localStorage.setItem('calendar_date', todayString);

      // Remove agent/call state from localStorage
      localStorage.removeItem('agentStatus');
      localStorage.removeItem('pendingMessage');
      localStorage.removeItem('pegaConversationID');
      localStorage.removeItem('isCallInProgress');
      localStorage.removeItem('callInfo');
      localStorage.removeItem('activeCallContact');
      
      // Remove call persistence state
      localStorage.removeItem('activeCallStatus');
      localStorage.removeItem('activeCallStartTime');
      localStorage.removeItem('activeCallConversationID');
      localStorage.removeItem('activeCallFirstResponse');
      localStorage.removeItem('activeCallGreeting');
      
      // Reset unread counts
      state.unreadCounts = {
        sms: 0,
        email: 0
      };
      
      // Clear PegaWebEmbed props to force rebuild
      state.employeePegaWebEmbedProps = null;
      
      // Don't clear baseConfig and channelConfig - this preserves apps and branding
      // The original comment was incorrect - we want to preserve the configuration
      // Only clear the dynamic state (messages, calls, etc.) not the configuration
      
      // Increment the counter to signal a reset to components
      state.resetCounter = state.resetCounter + 1;
    },
    clearDynamicState(state) {
      // Clear only dynamic state (messages, emails, agent state, calls)
      // PRESERVE the loaded config so apps/channels remain visible
      state.messages = state.defaultMessages || [];
      state.emails = state.defaultEmails || [];
      state.agentMessages = [];

      // Reset calendar date to today
      const today = new Date();
      const todayString = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
      state.calendarDate = todayString;
      state.calendarUpdateFromWebSocket = false; // Reset WebSocket flag

      // Reset agent state completely
      state.agentStatus = 'idle';
      state.pendingMessage = '';
      state.pegaConversationID = null;
      
      // Reset call state
      state.isCallInProgress = false;
      state.callInfo = null;
      state.activeCallContact = null;
      state.onboardingCallEvent = null;
      state.onboardingTourSteps = null;
      state.onboardingDefaultTourPending = false;

      // Persist this state back to localStorage
      localStorage.setItem('sms_messages', JSON.stringify(state.messages));
      localStorage.setItem('email_messages', JSON.stringify(state.emails));
      localStorage.setItem('agent_messages', JSON.stringify(state.agentMessages));
      localStorage.setItem('calendar_date', todayString);

      // Remove agent/call state from localStorage
      localStorage.removeItem('agentStatus');
      localStorage.removeItem('pendingMessage');
      localStorage.removeItem('pegaConversationID');
      localStorage.removeItem('isCallInProgress');
      localStorage.removeItem('callInfo');
      localStorage.removeItem('activeCallContact');
      
      // Remove call persistence state
      localStorage.removeItem('activeCallStatus');
      localStorage.removeItem('activeCallStartTime');
      localStorage.removeItem('activeCallConversationID');
      localStorage.removeItem('activeCallFirstResponse');
      localStorage.removeItem('activeCallGreeting');
      
      // Reset unread counts
      state.unreadCounts = {
        sms: 0,
        email: 0
      };
      
      // Clear PegaWebEmbed props to force rebuild and prevent React hook errors
      state.employeePegaWebEmbedProps = null;
      state.appPegaWebEmbedProps = null;
      
      // NOTE: DO NOT clear baseConfig or channelConfig here
      // We want to preserve the loaded config so apps remain visible
      
      // Increment the counter to signal a reset to components
      state.resetCounter = state.resetCounter + 1;
    },
    addMessage(state, action) {
      const message = action.payload;
      // Append the new message to the live messages array
      state.messages.push(message);
      // Persist updated messages to localStorage
      localStorage.setItem('sms_messages', JSON.stringify(state.messages));
      
      // Ensure unreadCounts exists
      if (!state.unreadCounts) {
        state.unreadCounts = { sms: 0, email: 0 };
      }
      
      // Increment unread count if not currently viewing SMS channel
      if (state.currentChannel !== 'sms') {
        state.unreadCounts.sms = (state.unreadCounts.sms || 0) + 1;
      }
    },
    addEmail(state, action) {
      const payload = action.payload;
      // Convert newlines in body to <br /> for HTML rendering
      let body = payload.body;
      if (typeof body === 'string') {
        body = body.replace(/\r?\n/g, '<br />');
      }
      // Get sender name from config channelBranding, fallback to "Customer Services"
      const fallbackSenderName = state.channelConfig?.email?.senderName || "Customer Services";
      // Add a unique ID and transform payload to match frontend data structure
      const newEmail = {
        id: `email_${Date.now()}`,
        read: false,
        subject: payload.subject,
        body: body,
        sender: { name: fallbackSenderName },
        date: payload.date || payload.timestamp || new Date().toISOString(),
        attachments: payload.attachments || (payload.attachment ? [{ name: 'Attachment', url: payload.attachment }] : []),
      };
      state.emails.unshift(newEmail);
      state.newlyReceivedEmailId = newEmail.id; // Flag this email as new for animation
      // Persist updated emails to localStorage
      localStorage.setItem('email_messages', JSON.stringify(state.emails));
      
      // Ensure unreadCounts exists
      if (!state.unreadCounts) {
        state.unreadCounts = { sms: 0, email: 0 };
      }
      
      // Increment unread count if not currently viewing email channel
      if (state.currentChannel !== 'email') {
        state.unreadCounts.email = (state.unreadCounts.email || 0) + 1;
      }
    },
    setEmailRead(state, action) {
      const emailId = action.payload;
      const email = state.emails.find(e => e.id === emailId);
      if (email) {
        email.read = true;
        // Persist updated emails to localStorage
        localStorage.setItem('email_messages', JSON.stringify(state.emails));
      }
    },
    setSelectedEmailId(state, action) {
      state.selectedEmailId = action.payload;
    },
    setIsViewingEmail(state, action) {
      state.isViewingEmail = action.payload;
      if (action.payload === false) {
        // When leaving detail view, clear selection
        state.selectedEmailId = null;
      }
    },
    viewEmail(state, action) {
      const emailId = action.payload;
      state.selectedEmailId = emailId;
      state.isViewingEmail = true;
      const email = state.emails.find(e => e.id === emailId);
      if (email) {
        email.read = true;
        // Persist updated emails to localStorage
        localStorage.setItem('email_messages', JSON.stringify(state.emails));
      }
    },
    clearNewEmailAnimation: (state) => {
      state.newlyReceivedEmailId = null;
    },
    setCallInProgress: (state, action) => {
      state.isCallInProgress = action.payload;
      // Persist call state to localStorage for cross-navigation persistence
      if (action.payload) {
        localStorage.setItem('isCallInProgress', 'true');
      } else {
        localStorage.removeItem('isCallInProgress');
      }
      // If the call is ending, clear the active contact, callInfo, callType, and stop vibrating
      if (!action.payload) {
        state.activeCallContact = null;
        state.callInfo = null;
        state.callType = 'voice';
        state.isVibrating = false;
        localStorage.removeItem('callInfo');
        localStorage.removeItem('activeCallContact');
      }
    },
    setCallType: (state, action) => {
      state.callType = action.payload; // 'voice' or 'video'
    },
    setActiveCallContact: (state, action) => {
      state.activeCallContact = action.payload;
      // Persist active call contact for cross-navigation persistence
      if (action.payload) {
        localStorage.setItem('activeCallContact', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('activeCallContact');
      }
    },
    setPendingMessage: (state, action) => {
      state.pendingMessage = action.payload;
      state.smsJustReceived = true;
    },
    commitPendingMessage(state) {
      // The animation is done. Commit the pending message to the list.
      if (state.pendingMessage) {
        state.messages.push(state.pendingMessage);
        // After updating the messages array, save it to localStorage.
        localStorage.setItem('sms_messages', JSON.stringify(state.messages));
      }
      // Reset the pending state.
      state.pendingMessage = null;
      state.smsJustReceived = false;
    },
    // Agent Assistant Reducers
    addAgentMessage(state, action) {
      state.agentMessages.push(action.payload);
      localStorage.setItem('agent_messages', JSON.stringify(state.agentMessages));
    },
    updateLastAgentMessage(state, action) {
      if (state.agentMessages.length > 0) {
        state.agentMessages[state.agentMessages.length - 1] = {
          ...state.agentMessages[state.agentMessages.length - 1],
          ...action.payload
        };
        localStorage.setItem('agent_messages', JSON.stringify(state.agentMessages));
      }
    },
    setAgentStatus(state, action) {
      const { isListening, isThinking, isSpeaking, agentStatus } = action.payload;
      state.isListening = isListening ?? state.isListening;
      state.isThinking = isThinking ?? state.isThinking;
      state.isSpeaking = isSpeaking ?? state.isSpeaking;
      if (agentStatus) state.agentStatus = agentStatus;
    },
    resetAgent(state) {
      state.agentMessages = [];
      state.pegaContextID = null;
      state.pegaConversationID = null;
      localStorage.setItem('agent_messages', JSON.stringify([]));
      localStorage.removeItem('pegaContextID');
      localStorage.removeItem('pegaConversationID');
    },
    setVibrating(state, action) {
      state.isVibrating = action.payload;
    },
    setCalendarDate(state, action) {
      state.calendarDate = action.payload;
      state.calendarUpdateFromWebSocket = true;
      // Persist calendar date to localStorage so it survives browser refresh
      localStorage.setItem('calendar_date', action.payload);
    },
    resetCalendarUpdateFromWebSocket(state) {
      state.calendarUpdateFromWebSocket = false;
    },
    setLastAnimatedCalendarDate(state, action) {
      state.lastAnimatedCalendarDate = action.payload;
    },
    setBrowserUrl: (state, action) => {
      state.browserUrl = action.payload; // <-- just set the top-level field
    },
    setAppUrl: (state, action) => {
      state.appUrl = action.payload;
    },
    setOnboardingCallEvent(state, action) {
      state.onboardingCallEvent = action.payload;
    },
    setOnboardingTourSteps(state, action) {
      state.onboardingTourSteps = action.payload;
    },
    setOnboardingDefaultTourPending(state, action) {
      state.onboardingDefaultTourPending = !!action.payload;
    },
    setCallInfo(state, action) {
      state.callInfo = action.payload;
      // Persist callInfo to localStorage
      if (action.payload) {
        localStorage.setItem('callInfo', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('callInfo');
      }
    },
    setAppChannelUrl: (state, action) => {
      const { url } = action.payload;
      if (state.channelConfig.app) {
        state.channelConfig.app.url = url;
      }
    },
    setEmployeePaneSrc: (state, action) => {
      state.employeePaneSrc = action.payload;
    },
    setPegaContextID: (state, action) => {
      state.pegaContextID = action.payload;
      localStorage.setItem('pegaContextID', action.payload);
    },
    setPegaConversationID: (state, action) => {
      state.pegaConversationID = action.payload;
      localStorage.setItem('pegaConversationID', action.payload);
    },
    clearPegaConversationID: (state) => {
      state.pegaConversationID = null;
      localStorage.removeItem('pegaConversationID');
    },
    requestAgentStopAndNavigate(state, action) {
      state.agentStopRequest = action.payload; // { nextChannel, viaNotification }
    },
    clearAgentStopRequest(state) {
      state.agentStopRequest = null;
    },
    setAppPegaWebEmbedProps: (state, action) => {
      // CRITICAL: When WebSocket sends /app event, it only includes navigation props
      // (pageClass, pageID, action), NOT infrastructure values (hostServer, applicationAlias).
      // We must preserve the existing infrastructure values and only update navigation props.
      const payload = action.payload;
      const existingProps = state.appPegaWebEmbedProps || {};
      
      // If payload is null, clear all props
      if (payload === null) {
        state.appPegaWebEmbedProps = null;
        console.log('📝 setAppPegaWebEmbedProps: Props cleared');
        return;
      }
      
      // Merge new props with existing, preserving infrastructure values
      state.appPegaWebEmbedProps = {
        // Start with existing props (preserves infrastructure values)
        ...existingProps,
        // Apply new props from WebSocket (navigation, theme, etc.)
        ...payload,
        // CRITICAL: Always enforce iframe container for isolation (multiple embeds on same page)
        container: 'iframe',
        // If theme exists in payload, deep clone it to ensure new reference
        ...(payload?.theme ? { theme: JSON.parse(JSON.stringify(payload.theme)) } : {})
      };
      
      console.log('📝 setAppPegaWebEmbedProps: Props merged', {
        hadExisting: !!existingProps,
        existingKeys: Object.keys(existingProps),
        payloadKeys: Object.keys(payload),
        hasTheme: !!state.appPegaWebEmbedProps.theme,
        hasThemeID: !!state.appPegaWebEmbedProps.themeID,
        container: state.appPegaWebEmbedProps.container,
        finalKeys: Object.keys(state.appPegaWebEmbedProps)
      });
    },
    setEmployeePegaWebEmbedProps: (state, action) => {
      // CRITICAL: When WebSocket sends /sidepanel event, it only includes navigation props
      // (pageClass, pageID, action), NOT infrastructure values (hostServer, applicationAlias).
      // We must preserve the existing infrastructure values and only update navigation props.
      const payload = action.payload;
      const existingProps = state.employeePegaWebEmbedProps || {};
      
      // If payload is null, clear all props
      if (payload === null) {
        state.employeePegaWebEmbedProps = null;
        console.log('📝 setEmployeePegaWebEmbedProps: Props cleared');
        return;
      }
      
      // Determine if this is a "Home" navigation (openPage with pyHome and no caseID/assignmentID)
      const isHomeNavigation = payload.action === 'openPage' && 
        (payload.pageID === 'pyHome' || payload.pageID === 'Home') &&
        !payload.caseID && !payload.caseId && 
        !payload.assignmentID && !payload.assignmentId;
      
      // Start with existing props (preserves infrastructure values)
      let mergedProps = { ...existingProps };
      
      // If navigating to Home, explicitly REMOVE case/assignment related properties
      // This ensures the address bar doesn't show stale case IDs
      if (isHomeNavigation) {
        delete mergedProps.caseID;
        delete mergedProps.caseId;
        delete mergedProps.assignmentID;
        delete mergedProps.assignmentId;
        console.log('📝 setEmployeePegaWebEmbedProps: Home navigation - clearing case/assignment props');
      }
      
      // Merge new props
      state.employeePegaWebEmbedProps = {
        ...mergedProps,
        // Apply new props from WebSocket (navigation, theme, etc.)
        ...payload,
        // CRITICAL: Always enforce iframe container for isolation (multiple embeds on same page)
        container: 'iframe',
        // If theme exists in payload, deep clone it to ensure new reference
        ...(payload?.theme ? { theme: JSON.parse(JSON.stringify(payload.theme)) } : {})
      };
      
      console.log('📝 setEmployeePegaWebEmbedProps: Props merged', {
        hadExisting: !!existingProps,
        existingKeys: Object.keys(existingProps),
        payloadKeys: Object.keys(payload),
        isHomeNavigation,
        hasTheme: !!state.employeePegaWebEmbedProps.theme,
        hasThemeID: !!state.employeePegaWebEmbedProps.themeID,
        container: state.employeePegaWebEmbedProps.container,
        finalKeys: Object.keys(state.employeePegaWebEmbedProps)
      });
    },
    setDxaProps: (state, action) => {
      // Set DXA navigation props from WebSocket messages
      // These props include: action, caseId, assignmentId, pageId, pageClass, etc.
      const payload = action.payload;
      
      // If payload is null, clear DXA props
      if (payload === null) {
        state.dxaProps = null;
        console.log('📝 setDxaProps: Props cleared');
        return;
      }
      
      // Set new DXA props
      state.dxaProps = {
        type: 'dxa',
        action: payload.action,
        pageId: payload.pageId,
        pageClass: payload.pageClass,
        authFlow: payload.authFlow,
        caseId: payload.caseId,
        assignmentId: payload.assignmentId,
        workId: payload.workId,
        additionalParams: payload.additionalParams || {}
      };
      
      console.log('📝 setDxaProps: Props set', {
        action: state.dxaProps.action,
        hasCaseId: !!state.dxaProps.caseId,
        hasAssignmentId: !!state.dxaProps.assignmentId,
        hasPageId: !!state.dxaProps.pageId,
        allKeys: Object.keys(state.dxaProps)
      });
    },
    setOnboardingEvents: (state, action) => {
      state.onboardingEvents = action.payload;
      console.log('🎓 Onboarding events set:', action.payload?.length || 0, 'events');
    },
    setHasSeenOnboarding: (state, action) => {
      state.hasSeenOnboarding = action.payload;
      if (action.payload) {
        localStorage.setItem('hasSeenOnboarding', 'true');
        console.log('✅ User has seen onboarding - saved to localStorage');
      } else {
        localStorage.removeItem('hasSeenOnboarding');
        console.log('🔄 Onboarding status reset');
      }
    },
    setSettingsModalOpen: (state, action) => {
      state.isSettingsModalOpen = action.payload;
      console.log('⚙️ Settings modal open state:', action.payload);
    },
    setBrandSwitcherOpen: (state, action) => {
      state.isBrandSwitcherOpen = action.payload;
    },
    // Event queue management for live modal
    queueEvent: (state, action) => {
      // Add event to queue when live modal is open
      // Ensure queuedEvents exists
      if (!state.queuedEvents) {
        state.queuedEvents = [];
      }
      console.log('🔵 Redux: queueEvent called, adding event:', action.payload.type, 'New queue length:', state.queuedEvents.length + 1);
      state.queuedEvents.push(action.payload);
    },
    processQueuedEvents: (state) => {
      // This will trigger processing of queued events
      // The actual processing happens in the WebSocket hook via useEffect
      // We just clear the queue here after they've been processed
      const queueLength = state.queuedEvents?.length || 0;
      console.log('🔵 Redux: processQueuedEvents called, clearing', queueLength, 'events');
      state.queuedEvents = [];
    },
    setHomeBackgroundImage: (state, action) => {
      // Background image now comes from brand in database
      state.homeBackgroundImage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfig.pending, (state) => {
        state.status = 'loading';
        state.configStatus = 'loading';
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.status = 'loaded';
        state.configStatus = 'loaded';
        const config = action.payload || {};
        
        // CRITICAL: Store the raw config in baseConfig so applyMergedConfig can use it
        state.baseConfig = config;
        
        console.log('📦 fetchConfig.fulfilled - Setting baseConfig:', {
          hostServer: config.hostServer,
          applicationAlias: config.applicationAlias,
          hasWebEmbed: !!(config.sidePanel || config.webEmbed)
        });

        // 1. Channels and channelConfig
        const baseChannelConfigData = config.channelConfig || {};
        const baseChannels = Object.keys(baseChannelConfigData);
        const storedChannelOverrides = JSON.parse(localStorage.getItem('channelConfigOverrides') || '{}');
        if (storedChannelOverrides.selfservice) {
          storedChannelOverrides.browser = { ...storedChannelOverrides.selfservice };
          delete storedChannelOverrides.selfservice;
          localStorage.setItem('channelConfigOverrides', JSON.stringify(storedChannelOverrides));
        }
        state.channels = baseChannels;
        state.channelConfig = baseChannels.reduce((acc, channelId) => {
          const baseConf = baseChannelConfigData[channelId] || {};
          const storedConf = storedChannelOverrides[channelId] || {};
          // Default 'visible' to true if not specified in either base or stored config
          const isVisible = typeof storedConf.visible !== 'undefined' 
            ? storedConf.visible 
            : (typeof baseConf.visible !== 'undefined' ? baseConf.visible : true);
          acc[channelId] = {
            ...baseConf,
            ...storedConf,
            visible: isVisible,
          };
          return acc;
        }, {});

        // 2. Default content (localStorage override if present)
        const processEmails = (emails, fallbackSenderName = "Customer Services") => {
          if (!emails) return null;
          return emails.map(email => {
            // Use sender name from email (config), fallback to provided fallback name
            const senderName = (typeof email.sender === 'object' ? email.sender.name : email.sender) || fallbackSenderName;
            return {
              ...email,
              sender: { name: senderName }
            };
          });
        };
        // Load live messages from localStorage (user data)
        const storedMessages = JSON.parse(localStorage.getItem('sms_messages') || 'null');
        const storedEmails = JSON.parse(localStorage.getItem('email_messages') || 'null');
        const storedAgentMessages = JSON.parse(localStorage.getItem('agent_messages') || 'null');
        const storedCalendarDate = localStorage.getItem('calendar_date') || null;
        
        // Restore call state from localStorage
        const storedCallInProgress = localStorage.getItem('isCallInProgress') === 'true';
        const storedActiveCallContact = JSON.parse(localStorage.getItem('activeCallContact') || 'null');
        const storedCallInfo = JSON.parse(localStorage.getItem('callInfo') || 'null');
        
        // Default content always comes from config (database), never from localStorage
        state.defaultMessages = config.defaultMessages || [];
        state.defaultEmails = processEmails(config.defaultEmails || [], config.channelBranding?.email?.senderName || "Customer Services");
        
        // If no live messages in localStorage, use defaults
        state.messages = storedMessages || state.defaultMessages;
        state.emails = processEmails(storedEmails, config.channelBranding?.email?.senderName || "Customer Services") || state.defaultEmails;
        state.agentMessages = storedAgentMessages || [];
        state.pegaContextID = localStorage.getItem('pegaContextID') || null;
        state.pegaConversationID = localStorage.getItem('pegaConversationID') || null;
        
        // Restore calendar date from localStorage or use today
        if (storedCalendarDate) {
          state.calendarDate = storedCalendarDate;
        } else {
          // Default to today if no stored date
          const today = new Date();
          state.calendarDate = today.toISOString().split('T')[0];
        }
        
        // Restore call state
        state.isCallInProgress = storedCallInProgress;
        state.activeCallContact = storedActiveCallContact;
        state.callInfo = storedCallInfo;

        // 3. Config keys: all come from database now
        const applicationAliasParam = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('applicationAlias') : null;
        // URL parameter overrides database value (for testing)
        state.applicationAlias = applicationAliasParam || config.applicationAlias || '';
        
        // Fix hostServer if it has double /prweb
        let hostServer = config.hostServer || '';
        if (hostServer.includes('/prweb/prweb')) {
          console.warn('⚠️ Detected double /prweb in hostServer, fixing:', hostServer);
          hostServer = hostServer.replace('/prweb/prweb', '/prweb');
        }
        state.hostServer = hostServer;
        state.persona = config.persona || '';
        state.pegaInfinityVersion = config.pegaInfinityVersion || '25.1.0';
        // Support both flat and nested structure for device bezel color
        // Only set if explicitly provided in config, otherwise keep initial state
        if (config.deviceBezelColor || config.colors?.deviceBezel) {
          state.deviceBezelColor = config.deviceBezelColor || config.colors.deviceBezel;
        }
        
        console.log('✅ fetchConfig.fulfilled - Infrastructure values set:', {
          hostServer: state.hostServer,
          applicationAlias: state.applicationAlias,
          backendBaseURL: state.backendBaseURL,
          persona: state.persona,
          deviceBezelColor: state.deviceBezelColor
        });

        state.enableSoundEffects = config.enableSoundEffects || false;
        state.homeBackgroundImage = config.homeBackgroundImage || '';
        // Support both flat and nested structure for gradient colors
        state.gradientColor1 = config.gradientColor1 || config.colors?.gradientColor1 || '#ee7752';
        state.gradientColor2 = config.gradientColor2 || config.colors?.gradientColor2 || '#e73c7e';
        state.gradientColor3 = config.gradientColor3 || config.colors?.gradientColor3 || '#23a6d5';
        state.gradientColor4 = config.gradientColor4 || config.colors?.gradientColor4 || '#23d5ab';
        state.sidePanelTitle = config.sidePanelTitle || 'Employee Panel';
        state.sidePanelIconSrc = config.sidePanelIconSrc || '';
        state.backendBaseURL = config.backendBaseURL || '';
        
        // Pega backend on/off. `"pega": false` disables all Pega behaviour.
        state.pegaEnabled = config.pega !== false;

        // SidePanel: plain iframe (has `url`) vs Pega Web Embed (infrastructure).
        {
          const sp = resolveSidePanel(config.sidePanel || config.webEmbed || null);
          state.webEmbedConfig = sp.webEmbedConfig;
          if (sp.employeePaneSrc) state.employeePaneSrc = sp.employeePaneSrc;
          state.configSidePanelTitle = sp.title || config.sidePanelTitle || null;
          state.livekit = config.livekit || null;
          if (state.configSidePanelTitle) state.sidePanelTitle = state.configSidePanelTitle;
        }

        console.log('📦 Config loaded - webEmbedConfig set:', {
          hasSidePanel: !!config.sidePanel,
          hasWebEmbed: !!config.webEmbed,
          webEmbedConfig: state.webEmbedConfig,
          clientId: state.webEmbedConfig?.clientId
        });
        
        // CRITICAL: Extract theme from config.webEmbed if present
        // This ensures the theme from config.json is applied on initial load, not just after brand is applied
        // The theme object takes priority over themeID
        const configWebEmbedTheme = config.webEmbed?.theme;
        const configWebEmbedThemeID = config.webEmbed?.themeID;
        
        if (configWebEmbedTheme || configWebEmbedThemeID) {
          console.log('🎨 Extracting webEmbed theme from config.json on initial load:', {
            hasTheme: !!configWebEmbedTheme,
            hasThemeID: !!configWebEmbedThemeID,
            themeKeys: configWebEmbedTheme ? Object.keys(configWebEmbedTheme) : null,
            themePreview: configWebEmbedTheme ? JSON.stringify(configWebEmbedTheme).substring(0, 200) : null
          });
          
          // Set webEmbedBranding with theme from config
          // This ensures the theme is available to SidePanel immediately on first load
          state.webEmbedBranding = {
            themeID: configWebEmbedThemeID,
            theme: configWebEmbedTheme ? JSON.parse(JSON.stringify(configWebEmbedTheme)) : undefined
          };
          
          console.log('🎨 WebEmbed branding set from config.json on initial load:', {
            hasThemeID: !!state.webEmbedBranding.themeID,
            hasTheme: !!state.webEmbedBranding.theme,
            themeKeys: state.webEmbedBranding.theme ? Object.keys(state.webEmbedBranding.theme) : null
          });
        } else {
          console.log('ℹ️ No webEmbed theme found in config.json');
        }
        
        // All config values are now managed centrally through Redux state
      })
      .addCase(fetchConfig.rejected, (state, action) => {
        state.status = 'failed';
        state.configStatus = 'failed';
        state.error = action.payload;
      })
      
      // Configuration loading
      .addCase(fetchConfiguration.pending, (state) => {
        state.configStatus = 'loading';
      })
      .addCase(fetchConfiguration.fulfilled, (state, action) => {
        const config = action.payload;
        console.log('📦 Applying configuration:', config);
        console.log('🔍 RAW config.hostServer from API:', config.hostServer);
        console.log('🔍 RAW config.environment?.hostServer from API:', config.environment?.hostServer);
        
        // Set configId from the API response or from the original request parameter
        state.configId = config.id || action.meta?.arg;
        state.configSource = 'api';
        
        console.log('🆔 Config ID set to:', state.configId);
        
        // CRITICAL: Store the raw config in baseConfig so applyMergedConfig can use it
        state.baseConfig = config;
        
        // Apply environment settings - support both nested and flat structure
        if (config.environment) {
          // Nested structure (old format)
          let envHostServer = config.environment.hostServer || state.hostServer;
          // Fix double /prweb if present
          if (envHostServer && envHostServer.includes('/prweb/prweb')) {
            console.warn('⚠️ fetchConfiguration: Detected double /prweb in environment.hostServer, fixing:', envHostServer);
            envHostServer = envHostServer.replace('/prweb/prweb', '/prweb');
          }
          state.hostServer = envHostServer;
          state.applicationAlias = config.environment.applicationAlias || state.applicationAlias;
          state.backendBaseURL = config.environment.backendBaseURL || state.backendBaseURL;
        } else {
          // Flat structure (database format)
          if (config.hostServer) {
            let flatHostServer = config.hostServer;
            // Fix double /prweb if present
            if (flatHostServer.includes('/prweb/prweb')) {
              console.warn('⚠️ fetchConfiguration: Detected double /prweb in hostServer, fixing:', flatHostServer);
              flatHostServer = flatHostServer.replace('/prweb/prweb', '/prweb');
              console.log('✅ fetchConfiguration: Fixed hostServer:', flatHostServer);
            }
            state.hostServer = flatHostServer;
            console.log('✅ fetchConfiguration: Set state.hostServer to:', state.hostServer);
          }
          if (config.applicationAlias) state.applicationAlias = config.applicationAlias;
          if (config.backendBaseURL) state.backendBaseURL = config.backendBaseURL;
        }
        
        // Apply other settings
        if (typeof config.debug !== 'undefined') state.debug = config.debug;
        if (typeof config.enableSoundEffects !== 'undefined') state.enableSoundEffects = config.enableSoundEffects;
        if (config.persona) state.persona = config.persona;
        if (config.pegaInfinityVersion) state.pegaInfinityVersion = config.pegaInfinityVersion;
        
        // Apply channel config - support both 'channels' and 'channelConfig'
        if (config.channels) {
          state.channelConfig = config.channels;
        } else if (config.channelConfig) {
          state.channelConfig = config.channelConfig;
        }
        
        // Apply default colors from config (before brand)
        if (config.colors && config.colors.gradient) {
          // Nested structure (legacy)
          state.gradientColor1 = config.colors.gradient.color1 || state.gradientColor1;
          state.gradientColor2 = config.colors.gradient.color2 || state.gradientColor2;
          state.gradientColor3 = config.colors.gradient.color3 || state.gradientColor3;
          state.gradientColor4 = config.colors.gradient.color4 || state.gradientColor4;
        } else if (config.colors && (config.colors.gradientColor1 || config.colors.gradientColor2)) {
          // Database structure (colors.gradientColor1, colors.gradientColor2, etc.)
          if (config.colors.gradientColor1) state.gradientColor1 = config.colors.gradientColor1;
          if (config.colors.gradientColor2) state.gradientColor2 = config.colors.gradientColor2;
          if (config.colors.gradientColor3) state.gradientColor3 = config.colors.gradientColor3;
          if (config.colors.gradientColor4) state.gradientColor4 = config.colors.gradientColor4;
        } else if (config.gradientColor1) {
          // Flat structure (gradientColor1, gradientColor2, etc.)
          state.gradientColor1 = config.gradientColor1 || state.gradientColor1;
          state.gradientColor2 = config.gradientColor2 || state.gradientColor2;
          state.gradientColor3 = config.gradientColor3 || state.gradientColor3;
          state.gradientColor4 = config.gradientColor4 || state.gradientColor4;
        }
        
        // Apply device bezel color - support both flat and nested structure
        if (config.deviceBezelColor) {
          state.deviceBezelColor = config.deviceBezelColor;
        } else if (config.colors?.deviceBezel) {
          state.deviceBezelColor = config.colors.deviceBezel;
        }
        
        // Load onboarding events from config - ALWAYS set, even if empty/missing
        // This prevents old onboarding events from a previous config persisting
        if (config.onboardingEvents && Array.isArray(config.onboardingEvents)) {
          state.onboardingEvents = config.onboardingEvents;
          console.log('🎓 Loaded onboarding events from config:', config.onboardingEvents.length, 'events');
        } else {
          // Config has no onboarding events - explicitly clear them
          console.log('🎓 Config has no onboarding events, clearing onboardingEvents array');
          state.onboardingEvents = [];
        }
        
        // Apply images from config
        if (config.images) {
          if (config.images.homeBackground) state.homeBackgroundImage = config.images.homeBackground;
          console.log('🖼️ Config images applied:', {
            homeBackground: state.homeBackgroundImage
          });
        } else if (config.homeBackgroundImage) {
          // Support legacy flat structure
          state.homeBackgroundImage = config.homeBackgroundImage;
        }
        
        // Pega backend on/off. `"pega": false` disables all Pega behaviour.
        state.pegaEnabled = config.pega !== false;

        // SidePanel: plain iframe (has `url`) vs Pega Web Embed (infrastructure).
        if (config.sidePanel || config.webEmbed) {
          const sp = resolveSidePanel(config.sidePanel || config.webEmbed);
          state.webEmbedConfig = sp.webEmbedConfig;
          if (sp.employeePaneSrc) state.employeePaneSrc = sp.employeePaneSrc;
          state.configSidePanelTitle = sp.title || config.sidePanelTitle || null;
          state.livekit = config.livekit || null;
          if (state.configSidePanelTitle) state.sidePanelTitle = state.configSidePanelTitle;
        }
        
        console.log('✅ Configuration applied:', {
          hostServer: state.hostServer,
          applicationAlias: state.applicationAlias,
          channels: Object.keys(state.channelConfig || {}).length,
          hasWebEmbedConfig: !!state.webEmbedConfig,
          colors: {
            color1: state.gradientColor1,
            color2: state.gradientColor2
          }
        });
        
        state.configStatus = 'loaded';
      })
      .addCase(fetchConfiguration.rejected, (state, action) => {
        state.configStatus = 'error';
        state.configError = action.payload;
      })
      
      // Brand loading
      .addCase(fetchBrand.pending, (state) => {
        state.brandStatus = 'loading';
      })
      .addCase(fetchBrand.fulfilled, (state, action) => {
        if (action.payload) {
          const brand = action.payload;
          console.log('🎨 fetchBrand.fulfilled - Received brand from API:', brand);
          console.log('🎨 fetchBrand.fulfilled - Brand keys:', Object.keys(brand));
          console.log('🎨 fetchBrand.fulfilled - brand.id:', brand.id, 'typeof:', typeof brand.id);
          console.log('🎨 fetchBrand.fulfilled - brand.webEmbed:', brand.webEmbed);
          console.log('🎨 fetchBrand.fulfilled - brand.brand_json:', brand.brand_json);
          console.log('🎨 fetchBrand.fulfilled - brand.brand_json?.webEmbed:', brand.brand_json?.webEmbed);
          
          // Store the brand object in Redux state
          state.currentBrand = brand;
          state.customerId = brand.id;
          
          // brand_name is a separate DB column — not in the JSONB blob returned by getBrand.
          // Look it up from availableBrands (populated by fetchAvailableBrands) which carries
          // the list-API response that DOES include the name column.
          const matchedBrand = state.availableBrands?.find(b => String(b.id) === String(brand.id));
          if (matchedBrand?.name) {
            state.brandName = matchedBrand.name;
            console.log('🏷️ fetchBrand.fulfilled - brandName set from availableBrands:', state.brandName);
          } else if (brand.name) {
            // Fallback: sometimes the brand object already carries name (e.g. when enriched)
            state.brandName = brand.name;
            console.log('🏷️ fetchBrand.fulfilled - brandName set from brand.name:', state.brandName);
          }
          
          // Use the centralized applyMergedConfig function to apply all brand overrides
          // This ensures consistency between manual brand application and API fetches
          applyMergedConfig(state);
          
          // Re-process existing emails with the new sender name from the brand
          // This updates all emails currently displayed when switching brands
          const emailChannelConfig = state.channelConfig?.email || {};
          const newSenderName = emailChannelConfig.senderName || "Customer Services";
          
          console.log('📧 fetchBrand.fulfilled - Email re-processing:', {
            emailChannelConfig,
            emailChannelConfigKeys: Object.keys(emailChannelConfig),
            newSenderName,
            emailsCount: state.emails?.length || 0
          });
          
          if (state.emails && state.emails.length > 0) {
            state.emails = state.emails.map(email => ({
              ...email,
              sender: { name: newSenderName }
            }));
            console.log('📧 fetchBrand.fulfilled - Updated existing emails with new sender name:', newSenderName, '- count:', state.emails.length);
            // Persist updated emails to localStorage
            localStorage.setItem('email_messages', JSON.stringify(state.emails));
          }
          
          console.log('✅ Brand applied via fetchBrand.fulfilled:', {
            name: brand.name,
            brandId: brand.id,
            hasWebEmbed: !!brand.webEmbed,
            hasWebEmbedTheme: !!brand.webEmbed?.theme,
            webEmbedBrandingSet: !!state.webEmbedBranding,
            webEmbedBrandingValue: state.webEmbedBranding,
            gradients: [state.gradientColor1, state.gradientColor2, state.gradientColor3, state.gradientColor4],
            emailSenderName: newSenderName
          });
        }
        state.brandStatus = 'loaded';
      })
      .addCase(fetchBrand.rejected, (state, action) => {
        state.brandStatus = 'error';
        state.brandError = action.payload;
      })
      
      // Available brands list
      .addCase(fetchAvailableBrands.fulfilled, (state, action) => {
        state.availableBrands = action.payload;
        // If we already have a current brand loaded, refresh brandName from the enriched list
        if (state.customerId) {
          const match = action.payload?.find(b => String(b.id) === String(state.customerId));
          if (match?.name) {
            state.brandName = match.name;
            console.log('🏷️ fetchAvailableBrands.fulfilled - brandName refreshed:', state.brandName);
          }
        }
        console.log('✅ Redux: availableBrands updated:', {
          count: state.availableBrands?.length || 0,
          brands: state.availableBrands
        });
      })
      
      // Available configurations list
      .addCase(fetchAvailableConfigurations.fulfilled, (state, action) => {
        state.availableConfigurations = action.payload;
        console.log('✅ Redux: availableConfigurations updated:', {
          count: state.availableConfigurations?.length || 0,
          configurations: state.availableConfigurations
        });
      })
      
      // Initialize from database (SettingsModal)
      .addCase(initializeFromDatabase.pending, (state) => {
        state.status = 'loading';
        state.configStatus = 'loading';
        state.brandStatus = 'loading';
      })
      .addCase(initializeFromDatabase.fulfilled, (state, action) => {
        console.log('✅ initializeFromDatabase.fulfilled', action.payload);
        const { config } = action.payload; // brand is available but unused
        
        state.status = 'loaded';
        state.configStatus = 'loaded';
        state.brandStatus = 'loaded';
        
        // Config and brand are already applied by their individual .fulfilled reducers
        // (fetchConfiguration.fulfilled and fetchBrand.fulfilled)
        // But we need to ensure configId is set from action.meta.arg
        // The configId comes from the original call parameters
        if (action.meta?.arg?.configId) {
          state.configId = action.meta.arg.configId;
          console.log('🆔 Set configId from initializeFromDatabase:', state.configId);
        } else if (config?.id) {
          // Fallback: try to get ID from config object
          state.configId = config.id;
          console.log('🆔 Set configId from config object:', state.configId);
        }
      })
      .addCase(initializeFromDatabase.rejected, (state, action) => {
        state.status = 'failed';
        state.configStatus = 'error';
        state.brandStatus = 'error';
        state.configError = action.payload;
        state.brandError = action.payload;
      });
  },
});

export const { 
  setChannel, 
  setSidePanelTitle,
  setSidePanelIconSrc,
  updateConfig, 
  updateChannelConfig, 
  updateDefaultContent, 
  toggleConfigModal, 
  toggleHelpModal,
  resetState, 
  clearDynamicState, // Export the new action
  addMessage, 
  addEmail, 
  setEmailRead,
  setSelectedEmailId,
  setIsViewingEmail,
  viewEmail,
  clearNewEmailAnimation,
  setCallInProgress,
  setCallType,
  setActiveCallContact,
  setPendingMessage,
  commitPendingMessage,
  addAgentMessage,
  updateLastAgentMessage,
  setAgentStatus,
  resetAgent,
  toggleFollowMode,
  toggleMobileStatusBar,
  toggleEmployeeEvents,
  toggleIPadDesktopLandscape,
  setFollowMode,
  setVibrating,
  setCalendarDate, // Export the new action
  resetCalendarUpdateFromWebSocket, // Export the new action
  setLastAnimatedCalendarDate, // Export the new action
  setBrowserUrl, // Export the new setBrowserUrl action
  setAppUrl,
  setCallInfo,
  setOnboardingCallEvent,
  setOnboardingTourSteps,
  setOnboardingDefaultTourPending,
  setAppChannelUrl, // Export the new setAppChannelUrl action
  setEmployeePaneSrc, // Export the new action
  setPegaContextID,
  setPegaConversationID,
  clearPegaConversationID,
  setPegaAccessToken,
  setAppPegaWebEmbedProps,
  setEmployeePegaWebEmbedProps,
  setDxaProps,
  setEmployeePaneVisibility,
  setHomeBackgroundImage,
  queueEvent,
  processQueuedEvents,
  applyBrand,
  clearBrand,
  setBaseConfig,
  setCurrentBrand,
  setAvailableBrands,
  setAvailableConfigurations,
  setOnboardingEvents,
  setHasSeenOnboarding,
  setSettingsModalOpen,
  setBrandSwitcherOpen,
  goBack,
} = channelSlice.actions;

// Selectors
export const selectHasSingleBrandAndStory = (state) => {
  const availableBrands = state.channel?.availableBrands || [];
  const availableConfigurations = state.channel?.availableConfigurations || [];
  return availableBrands.length === 1 && availableConfigurations.length === 1;
};

export const selectHasSingleBrand = (state) => {
  const availableBrands = state.channel?.availableBrands || [];
  return availableBrands.length === 1;
};

export default channelSlice.reducer;
